export interface FormatTooltipHtmlOptions {
  title: string;
  description?: string | null;
  tooltipCfg: any;
  letterSpacingAdjustment?: number;
  titleExtraStyles?: string;
  descriptionExtraStyles?: string;
  descriptionDir?: string;
}

const toNumber = (value: string | number | undefined, fallback = 0): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.replace('px', ''));
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
};

const joinStyles = (styles: Array<string | undefined | null>): string =>
  styles
    .filter(style => Boolean(style && String(style).length))
    .map(style => style!.trim().replace(/;+$/g, ''))
    .join(';');

export const formatTooltipHtml = ({
  title,
  description,
  tooltipCfg,
  letterSpacingAdjustment = -0.75,
  titleExtraStyles,
  descriptionExtraStyles,
  descriptionDir,
}: FormatTooltipHtmlOptions): string => {
  const baseLetterSpacing = toNumber(tooltipCfg?.letterSpacing, 0);
  const letterSpacingValue = `${baseLetterSpacing + letterSpacingAdjustment}px`;

  const headerFontSize = tooltipCfg?.headerFontSize ?? '15px';
  const headerLineHeight = toNumber(headerFontSize, 15) * 1.25;
  const headerStyles = joinStyles([
    'display:block',
    `letter-spacing:${letterSpacingValue}`,
    `font-weight:${tooltipCfg?.headerFontWeight}`,
    `font-size:${headerFontSize}`,
    `line-height:${headerLineHeight}px`,
    `margin-bottom:${tooltipCfg?.headerMarginBottom ?? '0px'}`,
    titleExtraStyles,
  ]);

  const headerHtml = `<div style='${headerStyles}'>${title}</div>`;

  if (!description) {
    return headerHtml;
  }

  const contentFontSize = tooltipCfg?.contentFontSize ?? '14px';
  const contentLineHeight = toNumber(contentFontSize, 14) * 1.25;
  const descriptionStyles = joinStyles([
    'display:block',
    `letter-spacing:${letterSpacingValue}`,
    `font-weight:${tooltipCfg?.contentFontWeight}`,
    `font-size:${contentFontSize}`,
    `line-height:${contentLineHeight}px`,
    descriptionExtraStyles,
  ]);

  const dirAttr = descriptionDir ? ` dir="${descriptionDir}"` : '';
  const descriptionHtml = `<div${dirAttr} style='${descriptionStyles}'>${description}</div>`;

  return `${headerHtml}${descriptionHtml}`;
};
